<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import mapboxgl from 'mapbox-gl';
import type { Map, GeoJSONSource, MapMouseEvent } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { Feature, Point, GeoJsonProperties } from 'geojson';

import type { IngredientId } from '@libs/trip-planner-interfaces';
import { CSSCustomProperty } from '@libs/ui';

import { MAPBOX_TOKEN } from '../token';
import { createSemboLocationPin, getNormalizedLngLatBounds } from '../shared';

import type { ProductSelectionMapData, PriceMarker } from './models';
import { PRICE_MARKERS_SETTINGS } from './models';
import { loadPriceMarkersPills } from './markerUtils';

interface ProductSelectionMapProps {
  data: ProductSelectionMapData;
  shouldShowZoomControls: boolean;
  mapStyles: string;
}

const props = defineProps<ProductSelectionMapProps>();

interface ProductSelectionMapEmits {
  (eventName: 'expose-instance', map: Map): void;
  (eventName: 'dragend'): void;
  (eventName: 'zoomend'): void;
  (
    eventName: 'marker-click',
    markerId: {
      code: string;
      ingredientId: IngredientId;
    }
  ): void;
  (eventName: 'cluster-click'): void;
}

const emit = defineEmits<ProductSelectionMapEmits>();

const mapContainer = ref<HTMLDivElement | null>(null);
const lastClickedMarkerId = ref<string | null>(null);
let map: Map | undefined = undefined;
let highlightMarker: mapboxgl.Marker | null = null;

const JITTER_STEP = 0.0001;
const mThreshold = 5;
const lThreshold = 7;
const selectedMThreshold = 7;
const selectedLThreshold = 9;
// NOTE: Only Mapbox Studio loaded fonts can be used here.
// For now I wasn't able to use either Cocogoose or CocoSharp
const MARKER_FONT = ['Open Sans Bold'];

function jitterOverlappingMarkers(markers: PriceMarker[]): PriceMarker[] {
  const coordMap = new globalThis.Map<string, number>();

  return markers.map((m) => {
    const key = `${m.point.latitude},${m.point.longitude}`;
    const index = coordMap.get(key) ?? 0;

    const offset = index > 0 ? JITTER_STEP * index : 0;

    coordMap.set(key, index + 1);

    return {
      ...m,
      point: {
        latitude: m.point.latitude + offset,
        longitude: m.point.longitude + offset,
      },
    };
  });
}

function mapMarkers(
  markers: PriceMarker[]
): Feature<Point, GeoJsonProperties>[] {
  return markers.reduce(
    (points: Feature<Point, GeoJsonProperties>[], marker: PriceMarker) => {
      const label =
        marker.state === 'selected' ||
        marker.state === 'selected-inspected' ||
        marker.state === 'included' ||
        marker.state === 'included-inspected'
          ? `✓ ${marker.label}`
          : marker.label;
      const point: Feature<Point, GeoJsonProperties> = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [marker.point.longitude, marker.point.latitude],
        },
        properties: {
          label,
          state: marker.state,
          productCode: marker.productCode,
          ingredientId: marker.ingredientId,
        },
      };

      if (marker.label) {
        points.push(point);
      }
      return points;
    },
    [] as Feature<Point, GeoJsonProperties>[]
  );
}

function setHighlightMarker(longitude?: number, latitude?: number): void {
  if (!map) {
    return;
  }

  if (highlightMarker) {
    highlightMarker.remove();
    highlightMarker = null;
  }

  if (longitude !== undefined && latitude !== undefined) {
    const element = createSemboLocationPin();
    highlightMarker = new mapboxgl.Marker({
      element,
      anchor: 'center',
    })
      .setLngLat([longitude, latitude])
      .addTo(map);
  }
}

function handleMarkerClick(event: MapMouseEvent): void {
  event.originalEvent.stopPropagation();

  const feature = event.features?.[0];
  if (!feature) {
    return;
  }

  const productCode = feature.properties?.productCode;
  const ingredientId = feature.properties?.ingredientId;

  if (!productCode || !ingredientId) {
    return;
  }

  const markerKey = `${productCode}-${ingredientId}`;

  if (lastClickedMarkerId.value === markerKey) {
    lastClickedMarkerId.value = null;
  } else {
    lastClickedMarkerId.value = markerKey;
    emit('marker-click', { code: productCode, ingredientId: ingredientId });
  }
}

function initializeMapLayers(): void {
  if (!map) {
    return;
  }

  if (map.getLayer('clusters')) {
    map.removeLayer('clusters');
  }
  if (map.getLayer('cluster-count')) {
    map.removeLayer('cluster-count');
  }
  if (map.getLayer('prices')) {
    map.removeLayer('prices');
  }
  if (map.getLayer('selected-marker-layer')) {
    map.removeLayer('selected-marker-layer');
  }
  if (map.getLayer('included-marker-layer')) {
    map.removeLayer('included-marker-layer');
  }
  if (map.getSource('markers')) {
    map.removeSource('markers');
  }
  if (map.getSource('selected-marker')) {
    map.removeSource('selected-marker');
  }
  if (map.getSource('included-marker')) {
    map.removeSource('included-marker');
  }

  loadPriceMarkersPills(map);

  map.addSource('markers', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: mapMarkers(jitterOverlappingMarkers(props.data.markers)),
    },
    cluster: true,
    clusterMaxZoom: 22,
    clusterRadius: PRICE_MARKERS_SETTINGS.clusterRadius,
    clusterMinPoints: PRICE_MARKERS_SETTINGS.clusterMinPoints,
  });

  map.addLayer({
    id: 'clusters',
    type: 'circle',
    source: 'markers',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': CSSCustomProperty.colorWhite,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 30, 24],
      'circle-stroke-color': CSSCustomProperty.colorNeutral30,
      'circle-stroke-width': 1,
    },
  });

  map.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: 'markers',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': MARKER_FONT,
      'text-size': 14,
    },
    paint: { 'text-color': CSSCustomProperty.colorSoftBlack },
  });

  map.addLayer({
    id: 'prices',
    type: 'symbol',
    source: 'markers',
    filter: ['!', ['has', 'point_count']],
    layout: {
      'icon-image': [
        'concat',
        [
          'match',
          ['get', 'state'],
          'normal',
          'price-pill-normal-',
          'selected',
          'price-pill-selected-',
          'inspected',
          'price-pill-inspected-',
          'selected-inspected',
          'price-pill-selected-inspected-',
          'previously-seen',
          'price-pill-previously-seen-',
          'price-pill-normal-',
        ],
        [
          'case',
          ['<=', ['length', ['get', 'label']], mThreshold],
          's',
          ['<=', ['length', ['get', 'label']], lThreshold],
          'm',
          'l',
        ],
      ],
      'icon-allow-overlap': PRICE_MARKERS_SETTINGS.iconAllowOverlap,
      'icon-ignore-placement': PRICE_MARKERS_SETTINGS.iconIgnorePlacement,
      'text-allow-overlap': PRICE_MARKERS_SETTINGS.textAllowOverlap,
      'text-ignore-placement': PRICE_MARKERS_SETTINGS.textIgnorePlacement,
      'icon-size': 1,
      'icon-anchor': 'center',
      'text-field': ['get', 'label'],
      'text-font': MARKER_FONT,
      'text-size': 14,
      'text-anchor': 'center',
    },
    paint: {
      'text-color': [
        'match',
        ['get', 'state'],
        'normal',
        CSSCustomProperty.colorSoftBlack,
        'inspected',
        'white',
        'selected',
        'white',
        'selected-inspected',
        'white',
        'previously-seen',
        CSSCustomProperty.colorNeutral60,
        CSSCustomProperty.colorSoftBlack,
      ],
    },
  });

  map.addSource('selected-marker', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: mapMarkers(
        jitterOverlappingMarkers(props.data.selectedMarkers)
      ),
    },
  });

  map.addLayer({
    id: 'selected-marker-layer',
    type: 'symbol',
    source: 'selected-marker',
    layout: {
      'icon-image': [
        'concat',
        [
          'match',
          ['get', 'state'],
          'selected',
          'price-pill-selected-',
          'selected-inspected',
          'price-pill-selected-inspected-',
          'price-pill-selected-',
        ],
        [
          'case',
          ['<=', ['length', ['get', 'label']], selectedMThreshold],
          's',
          ['<=', ['length', ['get', 'label']], selectedLThreshold],
          'm',
          'l',
        ],
      ],
      'icon-size': 1,
      'icon-anchor': 'center',
      'text-field': ['get', 'label'],
      'text-font': MARKER_FONT,
      'text-size': 14,
      'text-anchor': 'center',
      'icon-allow-overlap': PRICE_MARKERS_SETTINGS.iconAllowOverlap,
      'icon-ignore-placement': PRICE_MARKERS_SETTINGS.iconIgnorePlacement,
      'text-allow-overlap': PRICE_MARKERS_SETTINGS.textAllowOverlap,
      'text-ignore-placement': PRICE_MARKERS_SETTINGS.textIgnorePlacement,
    },
    paint: {
      'text-color': CSSCustomProperty.colorWhite,
    },
  });

  map.addSource('included-marker', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: mapMarkers(props.data.includedMarkers),
    },
  });

  map.addLayer({
    id: 'included-marker-layer',
    type: 'symbol',
    source: 'included-marker',
    layout: {
      'icon-image': [
        'concat',
        [
          'match',
          ['get', 'state'],
          'included',
          'price-pill-included-',
          'included-inspected',
          'price-pill-included-inspected-',
          'price-pill-included-',
        ],
        [
          'case',
          ['<=', ['length', ['get', 'label']], selectedMThreshold],
          's',
          ['<=', ['length', ['get', 'label']], selectedLThreshold],
          'm',
          'l',
        ],
      ],
      'icon-size': 1,
      'icon-anchor': 'center',
      'text-field': ['get', 'label'],
      'text-font': MARKER_FONT,
      'text-size': 14,
      'text-anchor': 'center',
      'icon-allow-overlap': PRICE_MARKERS_SETTINGS.iconAllowOverlap,
      'icon-ignore-placement': PRICE_MARKERS_SETTINGS.iconIgnorePlacement,
      'text-allow-overlap': PRICE_MARKERS_SETTINGS.textAllowOverlap,
      'text-ignore-placement': PRICE_MARKERS_SETTINGS.textIgnorePlacement,
    },
    paint: {
      'text-color': CSSCustomProperty.colorWhite,
    },
  });

  map.off('click', 'prices', handleMarkerClick);
  map.off('click', 'selected-marker-layer', handleMarkerClick);
  map.off('click', 'included-marker-layer', handleMarkerClick);
  map.on('click', 'prices', handleMarkerClick);
  map.on('click', 'selected-marker-layer', handleMarkerClick);
  map.on('click', 'included-marker-layer', handleMarkerClick);
}

onMounted(() => {
  mapboxgl.accessToken = MAPBOX_TOKEN;

  if (mapContainer.value === null) {
    return;
  }

  const points = props.data.markers.map((marker) => marker.point);
  const bounds = getNormalizedLngLatBounds(points);

  map = new mapboxgl.Map({
    container: mapContainer.value,
    maxZoom: 22,
    minZoom: 4,
    style: props.mapStyles,
    bounds: props.data.markers.length ? bounds : undefined,
    fitBoundsOptions: { padding: 24 },
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();

  if (props.shouldShowZoomControls) {
    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      'bottom-right'
    );
  }

  map.on('dragend', () => {
    emit('dragend');
  });

  map.on('zoomend', () => {
    emit('zoomend');
  });

  map.on('load', () => {
    initializeMapLayers();
    emit('expose-instance', map as Map);
  });

  map.on('style.load', () => {
    initializeMapLayers();
  });

  map.on('click', 'clusters', (event: MapMouseEvent) => {
    if (!map) {
      return;
    }

    const topFeatures = map.queryRenderedFeatures(event.point, {
      layers: ['selected-marker-layer', 'prices'],
    });

    if (topFeatures.length > 0) {
      return;
    }

    const clusterFeatures = map.queryRenderedFeatures(event.point, {
      layers: ['clusters'],
    });
    if (!clusterFeatures.length) {
      return;
    }

    const cluster = clusterFeatures[0];

    if (!cluster) {
      return;
    }

    const clusterId = cluster.properties?.cluster_id as number | undefined;
    const source = map.getSource('markers') as GeoJSONSource;

    if (!source || !clusterId) {
      return;
    }

    const geometry = cluster.geometry;
    if (geometry?.type !== 'Point') {
      return;
    }

    const center = geometry.coordinates as [number, number];

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || !map) {
        return;
      }

      map.easeTo({
        center,
        zoom: zoom ?? undefined,
        duration: 800,
      });
      emit('cluster-click');
    });
  });

  map.on('mouseenter', 'clusters', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', 'clusters', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = '';
  });

  map.on('mouseenter', 'prices', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', 'prices', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = '';
  });

  map.on('mouseenter', 'selected-marker-layer', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', 'selected-marker-layer', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = '';
  });

  map.on('mouseenter', 'included-marker-layer', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', 'included-marker-layer', () => {
    if (!map) {
      return;
    }
    map.getCanvas().style.cursor = '';
  });
});

watch(
  () => props.data.markers,
  (newMarkers) => {
    if (!map) {
      return;
    }

    const source = map.getSource('markers') as GeoJSONSource;

    if (!source) {
      return;
    }
    source.setData({
      type: 'FeatureCollection',
      features: mapMarkers(jitterOverlappingMarkers(newMarkers)),
    });
  },
  { deep: true }
);

watch(
  () => props.data.highlightMarker,
  (marker) => {
    if (!map) {
      return;
    }

    if (marker?.longitude !== undefined && marker?.latitude !== undefined) {
      setHighlightMarker(marker.longitude, marker.latitude);
    } else {
      setHighlightMarker();
    }
  },
  { deep: true }
);

watch(
  () => props.data.selectedMarkers,
  (markers) => {
    if (!map) {
      return;
    }

    const source = map.getSource('selected-marker') as GeoJSONSource;

    if (!source) {
      return;
    }
    source.setData({
      type: 'FeatureCollection',
      features: mapMarkers(jitterOverlappingMarkers(markers)),
    });
  },
  { deep: true }
);

watch(
  () => props.data.includedMarkers,
  (markers) => {
    if (!map) {
      return;
    }

    const source = map.getSource('included-marker') as GeoJSONSource;

    if (!source) {
      return;
    }

    const features = mapMarkers(markers);

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  },
  { deep: true }
);
</script>

<template>
  <div class="product-selection-map">
    <div ref="mapContainer" class="map-container" />
  </div>
</template>

<style lang="scss" scoped>
.product-selection-map {
  position: relative;
  width: 100%;
  height: 100%;

  .map-container {
    width: 100%;
    height: 100%;
  }
}
</style>

<style lang="scss">
// NOTE: Focus on map canvas looks bad
.mapboxgl-canvas:focus {
  outline: none;
}

// NOTE: This class name is hardcoded into highlight pin SVG
.highlight-pulse {
  animation: pulseHighlight 1.2s ease-in-out infinite;
  transform-origin: center center;
}

@keyframes pulseHighlight {
  0% {
    transform: translateY(-5px);
  }
  50% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(-5px);
  }
}
</style>
